import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MessageCircleOff, Send } from 'lucide-react'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { Button } from '@/common/ui/button'
import { Progress } from '@/common/ui/progress'
import { useQueryChatQuota } from '@/modules/chat/api/hooks'

const OUT_THRESHOLD = 100
const DISMISSED_KEY = 'line-quota-out-dismissed'

export function LineQuotaWarningDialog() {
	const { data } = useQueryChatQuota()
	const location = useLocation()
	const [open, setOpen] = useState(false)

	const quotaInfo = useMemo(() => {
		if (!data?.quota) return null
		const { totalUsage, quotaValue, remaining } = data.quota
		const percentage = Math.round((totalUsage / quotaValue) * 100)
		return { totalUsage, quotaValue, remaining, percentage }
	}, [data])

	const isOut = quotaInfo ? quotaInfo.percentage >= OUT_THRESHOLD : false

	useEffect(() => {
		if (!isOut) {
			setOpen(false)
			return
		}
		const dismissed = sessionStorage.getItem(DISMISSED_KEY) === '1'
		if (!dismissed) {
			setOpen(true)
		}
	}, [isOut, location.pathname])

	const handleDismiss = () => {
		sessionStorage.setItem(DISMISSED_KEY, '1')
		setOpen(false)
	}

	if (!quotaInfo || !isOut) return null

	return (
		<AlertDialog open={open}>
			<AlertDialogContent
				className="max-w-md border-2 border-destructive"
				onEscapeKeyDown={(e) => e.preventDefault()}
			>
				<AlertDialogHeader>
					<div className="flex items-start gap-3">
						<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-destructive/20">
							<MessageCircleOff className="h-6 w-6 text-destructive" />
						</div>
						<div className="flex-1 space-y-1">
							<AlertDialogTitle className="text-destructive">
								โควต้าข้อความ LINE หมดแล้ว
							</AlertDialogTitle>
							<AlertDialogDescription>
								ไม่สามารถส่ง Push Message ผ่าน LINE ได้จนกว่าจะมีโควต้าเพิ่ม
							</AlertDialogDescription>
						</div>
					</div>
				</AlertDialogHeader>

				<div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
					<Send className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
					<div className="space-y-0.5 text-sm">
						<p className="font-semibold text-destructive">
							แชท LINE หยุดส่งชั่วคราว
						</p>
						<p className="text-muted-foreground">
							Push Message ทุกประเภท (Broadcast, แจ้งเตือน)
							จะไม่ถูกส่งจนกว่าจะเติมโควต้า
						</p>
					</div>
				</div>

				<div className="space-y-3">
					<div className="flex items-center justify-between text-sm">
						<span>
							ใช้ไปแล้ว{' '}
							<span className="font-bold text-destructive">
								{quotaInfo.percentage}%
							</span>
						</span>
						<span className="text-muted-foreground">
							คงเหลือ {quotaInfo.remaining.toLocaleString()} /{' '}
							{quotaInfo.quotaValue.toLocaleString()} ข้อความ
						</span>
					</div>
					<Progress
						value={quotaInfo.percentage}
						className="h-2 [&>div]:bg-destructive"
					/>
				</div>

				<AlertDialogFooter>
					<Button variant="outline" onClick={handleDismiss}>
						ปิด (จะเตือนอีกครั้งเมื่อเปลี่ยนหน้า)
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
